/**
 * Graficele dashboard-ului — SVG inline, fără nicio bibliotecă.
 *
 * De ce fără bibliotecă: un dashboard de restaurant afișează patru tipuri de grafic (evoluție
 * zilnică, ore, comparații, bare de proporție). Pentru atât, o dependență de 300 KB de charting ar
 * încărca pagina publică și dashboard-ul degeaba, iar controlul tipografic (fonturi, culori din
 * design tokens, formatarea numerelor în română) ar fi mai greu, nu mai ușor. SVG-ul de aici este
 * scris cu `viewBox`, deci se scalează pe orice ecran fără măsurători în JavaScript.
 */
import { useId } from 'react';
import { cx } from './ui.js';

export interface SeriesPoint {
  day: string;
  views?: number;
  scans?: number;
  uniqueVisitors?: number;
  avgSeconds?: number;
  uptime?: number;
  heartbeats?: number;
}

/** Eticheta scurtă a zilei: „24 sep". */
function shortDay(day: string): string {
  const date = new Date(`${day}T12:00:00Z`);
  return new Intl.DateTimeFormat('ro-RO', { day: 'numeric', month: 'short' }).format(date);
}

function niceMax(value: number): number {
  if (value <= 0) return 10;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  return Math.ceil(value / magnitude) * magnitude;
}

/* ─────────────────────────── evoluția zilnică (arie + linii) ─────────────────────────── */

export function TrendChart({
  points,
  series,
  height = 220,
  formatValue = (value: number) => String(value),
}: {
  points: SeriesPoint[];
  series: Array<{ key: 'views' | 'scans' | 'uniqueVisitors' | 'uptime' | 'heartbeats'; label: string; color: string; area?: boolean }>;
  height?: number;
  formatValue?: (value: number) => string;
}) {
  const gradientId = useId();
  const width = 720;
  const padding = { top: 16, right: 8, bottom: 26, left: 8 };
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  const maximum = niceMax(
    Math.max(...points.flatMap((point) => series.map((line) => Number(point[line.key] ?? 0))), 0),
  );

  const x = (index: number) => padding.left + (points.length <= 1 ? innerWidth / 2 : (index / (points.length - 1)) * innerWidth);
  const y = (value: number) => padding.top + innerHeight - (value / maximum) * innerHeight;

  // Doar câteva etichete pe axa X: pe mobil, 30 de zile ar deveni ilizibile.
  const labelStep = Math.max(1, Math.ceil(points.length / 7));

  return (
    <div className="w-full overflow-hidden">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={series.map((line) => line.label).join(', ')}
        className="h-auto w-full"
        preserveAspectRatio="none"
      >
        <defs>
          {series.map((line) => (
            <linearGradient key={line.key} id={`${gradientId}-${line.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={line.color} stopOpacity="0.28" />
              <stop offset="100%" stopColor={line.color} stopOpacity="0.02" />
            </linearGradient>
          ))}
        </defs>

        {/* Linii de referință orizontale — ajută ochiul să citească valoarea, fără a aglomera. */}
        {[0, 0.5, 1].map((ratio) => (
          <line
            key={ratio}
            x1={padding.left}
            x2={width - padding.right}
            y1={padding.top + innerHeight * ratio}
            y2={padding.top + innerHeight * ratio}
            stroke="var(--color-ink-100)"
            strokeWidth="1"
          />
        ))}

        {series.map((line) => {
          const values = points.map((point) => Number(point[line.key] ?? 0));
          const path = values.map((value, index) => `${index === 0 ? 'M' : 'L'}${x(index)},${y(value)}`).join(' ');
          const area = `${path} L${x(values.length - 1)},${padding.top + innerHeight} L${x(0)},${padding.top + innerHeight} Z`;
          return (
            <g key={line.key}>
              {line.area === false ? null : <path d={area} fill={`url(#${gradientId}-${line.key})`} />}
              <path d={path} fill="none" stroke={line.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              {points.map((point, index) => (
                <circle key={point.day} cx={x(index)} cy={y(values[index]!)} r="3" fill="var(--color-surface)" stroke={line.color} strokeWidth="2">
                  <title>{`${shortDay(point.day)}: ${formatValue(values[index]!)}`}</title>
                </circle>
              ))}
            </g>
          );
        })}

        {points.map((point, index) =>
          index % labelStep === 0 ? (
            <text
              key={point.day}
              x={x(index)}
              y={height - 8}
              textAnchor={index === 0 ? 'start' : index >= points.length - 1 ? 'end' : 'middle'}
              className="fill-ink-400"
              style={{ fontSize: 11 }}
            >
              {shortDay(point.day)}
            </text>
          ) : null,
        )}
      </svg>
    </div>
  );
}

/* ─────────────────────────── bare verticale (ore din zi) ─────────────────────────── */

export function HourBars({ hours, label = 'activitate' }: { hours: number[]; label?: string }) {
  const maximum = Math.max(...hours, 1);
  const busiest = hours.indexOf(maximum);
  return (
    <div>
      <div className="flex h-40 items-end gap-[3px]" role="img" aria-label={`Distribuția pe ore a ${label}`}>
        {hours.map((value, hour) => (
          <div key={hour} className="group relative flex flex-1 flex-col justify-end" style={{ height: '100%' }}>
            <div
              className={cx(
                'w-full rounded-t-sm transition-colors',
                hour === busiest ? 'bg-accent-500' : 'bg-brand-500/70 group-hover:bg-brand-600',
              )}
              style={{ height: `${Math.max(2, (value / maximum) * 100)}%` }}
              title={`${String(hour).padStart(2, '0')}:00 — ${value} ${label}`}
            />
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-caption text-ink-400">
        <span>00:00</span>
        <span>06:00</span>
        <span>12:00</span>
        <span>18:00</span>
        <span>23:00</span>
      </div>
      {maximum > 0 ? (
        <p className="mt-1 text-caption text-ink-600">
          Vârful zilei: <span className="nums font-medium text-ink-950">{String(busiest).padStart(2, '0')}:00</span> —{' '}
          {maximum} {label}
        </p>
      ) : null}
    </div>
  );
}

/* ─────────────────────────── bare orizontale (comparații) ─────────────────────────── */

export function BreakdownBars({
  rows,
  emptyLabel = 'Nu există date încă.',
}: {
  rows: Array<{ key: string; label: string; value: number }>;
  emptyLabel?: string;
}) {
  if (!rows.length) return <p className="text-body-s text-ink-400">{emptyLabel}</p>;
  const maximum = Math.max(...rows.map((row) => row.value), 1);

  return (
    <ul className="flex flex-col gap-3">
      {rows.map((row) => (
        <li key={row.key} className="flex flex-col gap-1">
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate text-body-s text-ink-800">{row.label}</span>
            <span className="nums text-body-s font-medium text-ink-950">{row.value}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-surface-3">
            <div className="h-full rounded-full bg-brand-500" style={{ width: `${(row.value / maximum) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ─────────────────────────── procent din maxim (pentru tabele) ─────────────────────────── */

export function ShareBar({ value, maximum, tone = 'brand' }: { value: number; maximum: number; tone?: 'brand' | 'accent' }) {
  const ratio = maximum > 0 ? (value / maximum) * 100 : 0;
  return (
    <div className="h-1.5 w-full min-w-16 overflow-hidden rounded-full bg-surface-3">
      <div
        className={cx('h-full rounded-full', tone === 'accent' ? 'bg-accent-500' : 'bg-brand-500')}
        style={{ width: `${Math.max(ratio, value > 0 ? 3 : 0)}%` }}
      />
    </div>
  );
}
